import { getLocale } from "@plannello/i18n/runtime";
import type { ItemLite, MilestoneView } from "@plannello/shared";
import type { TimelineRows, TimelineZoom } from "@/components/views/viewState";
import type { Catalog } from "@/data/catalog";
import { compareMilestones } from "../milestoneOrder";

/*
 * Timeline model: calendar days as integers (days since 1970-01-01, UTC, so DST never shifts a
 * bar), the scale per zoom level, the visible range, header ticks and the rows per mode.
 */

const MS_DAY = 86_400_000;

export function dayOf(iso: string): number {
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  return Math.floor(Date.UTC(y!, m! - 1, d!) / MS_DAY);
}

export function isoOf(day: number): string {
  return new Date(day * MS_DAY).toISOString().slice(0, 10);
}

export function todayDay(): number {
  const n = new Date();
  return Math.floor(Date.UTC(n.getFullYear(), n.getMonth(), n.getDate()) / MS_DAY);
}

/** 0 = Monday ... 6 = Sunday. */
export const weekday = (day: number) => (((day + 3) % 7) + 7) % 7;

export const DAY_WIDTH: Record<TimelineZoom, number> = { weeks: 32, months: 9, quarters: 3.2 };
/** Keyboard step (Alt+Arrow) per zoom: one day when days are visible, a week otherwise. */
export const KEY_STEP: Record<TimelineZoom, number> = { weeks: 1, months: 7, quarters: 7 };
/** Length of a bar created by dropping an unscheduled item. */
export const DEFAULT_SPAN_DAYS = 7;

const utc = (day: number) => new Date(day * MS_DAY);
const fmt = (opts: Intl.DateTimeFormatOptions) => new Intl.DateTimeFormat(getLocale(), { ...opts, timeZone: "UTC" });

export function formatDay(day: number, withYear = true): string {
  return fmt({ day: "numeric", month: "short", ...(withYear ? { year: "numeric" } : {}) }).format(utc(day));
}

/** "3 Mar to 14 Mar 2026" (the year once when both are in the same year). */
export function formatSpan(start: number, end: number, to: string): string {
  if (start === end) return formatDay(start);
  const sameYear = utc(start).getUTCFullYear() === utc(end).getUTCFullYear();
  return `${formatDay(start, !sameYear)} ${to} ${formatDay(end)}`;
}

export type Tick = { day: number; label: string; major?: boolean };

function firstOfMonth(day: number): number {
  const d = utc(day);
  return Math.floor(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1) / MS_DAY);
}

function addMonths(day: number, n: number): number {
  const d = utc(day);
  return Math.floor(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + n, 1) / MS_DAY);
}

/** Two header tiers: top (months, or quarters when zoomed out) and bottom (days, weeks or months). */
export function headerTicks(start: number, end: number, zoom: TimelineZoom): { top: Tick[]; bottom: Tick[] } {
  const top: Tick[] = [];
  const bottom: Tick[] = [];
  const monthFmt = fmt({ month: "long", year: "numeric" });
  const shortMonth = fmt({ month: "short" });
  if (zoom === "quarters") {
    for (let d = firstOfMonth(start); d <= end; d = addMonths(d, 1)) {
      const date = utc(d);
      bottom.push({ day: d, label: shortMonth.format(date) });
      if (date.getUTCMonth() % 3 === 0 || top.length === 0)
        top.push({ day: d, label: `Q${Math.floor(date.getUTCMonth() / 3) + 1} ${date.getUTCFullYear()}`, major: true });
    }
    return { top, bottom };
  }
  for (let d = firstOfMonth(start); d <= end; d = addMonths(d, 1))
    top.push({ day: d, label: monthFmt.format(utc(d)), major: true });
  if (zoom === "weeks") {
    for (let d = start; d <= end; d++)
      bottom.push({ day: d, label: String(utc(d).getUTCDate()), major: weekday(d) === 0 });
  } else {
    let d = start - weekday(start);
    for (; d <= end; d += 7) bottom.push({ day: d, label: String(utc(d).getUTCDate()) });
  }
  return { top, bottom };
}

/* ---------------------------------------------------------------- rows */

export type Span = {
  start: number;
  end: number /** One end was missing and copied from the other. */;
  open?: "start" | "end";
};

/** An item's own span from start and due dates (a missing end mirrors the other end). */
export function itemSpan(item: Pick<ItemLite, "startDate" | "dueDate">): Span | null {
  const s = item.startDate ? dayOf(item.startDate) : null;
  const e = item.dueDate ? dayOf(item.dueDate) : null;
  if (s === null && e === null) return null;
  if (s === null) return { start: e!, end: e!, open: "start" };
  if (e === null) return { start: s, end: s, open: "end" };
  return s <= e ? { start: s, end: e } : { start: e, end: s };
}

export type TimelineRow =
  | {
      kind: "section";
      id: string;
      milestone: MilestoneView | null;
      count: number;
      collapsed: boolean;
      /** Earliest start of its dated items (for the section's span). */
      span: Span | null;
    }
  | {
      kind: "item";
      id: string;
      item: ItemLite;
      depth: number;
      childCount: number;
      expanded: boolean;
      /** Span derived from children when the item has no dates of its own. */
      rollup: Span | null;
      /** Milestone target the undated item is anchored to (milestone mode). */
      anchor: number | null;
    };

const byStart = (a: ItemLite, b: ItemLite) => {
  const sa = itemSpan(a);
  const sb = itemSpan(b);
  if (sa && sb) return sa.start - sb.start || sa.end - sb.end || a.key.localeCompare(b.key);
  if (sa) return -1;
  if (sb) return 1;
  return a.rank < b.rank ? -1 : a.rank > b.rank ? 1 : 0;
};

function rollupOf(children: readonly ItemLite[]): Span | null {
  let lo = Number.POSITIVE_INFINITY;
  let hi = Number.NEGATIVE_INFINITY;
  for (const c of children) {
    const s = itemSpan(c);
    if (!s) continue;
    lo = Math.min(lo, s.start);
    hi = Math.max(hi, s.end);
  }
  return Number.isFinite(lo) ? { start: lo, end: hi } : null;
}

export type BuildOpts = {
  mode: TimelineRows;
  showSubItems: boolean;
  /** Collapsed epics (item ids) and milestone sections (`ms:<id>`). */
  collapsed: ReadonlySet<string>;
  /** Every cached item of the scope (children of epics are found here even when filtered out of `items`). */
  all: readonly ItemLite[];
};

export type BuiltRows = {
  rows: TimelineRow[];
  /** Filtered items with no dates of their own (the Unscheduled panel). */
  unscheduled: ItemLite[];
  /** Items whose dates put a bar on the canvas. */
  scheduled: number;
};

/**
 * Rows for one timeline mode.
 *   epics      initiatives and epics (level >= 2) from the filtered items, their children under
 *              them (sub-items on), parents without dates get a rollup span from their children
 *   all        every filtered item with a date, by start; children under dated parents
 *   milestone  a section per open milestone (and any milestone with matching items), then
 *              "No milestone"; undated items anchor to their milestone's target date
 */
export function buildRows(items: readonly ItemLite[], catalog: Catalog, opts: BuildOpts): BuiltRows {
  const unscheduled = items.filter((i) => !i.startDate && !i.dueDate).sort(byStart);
  const scheduled = items.length - unscheduled.length;
  const childrenOf = new Map<string, ItemLite[]>();
  if (opts.showSubItems) {
    const inScope = new Set(items.map((i) => i.id));
    for (const i of opts.all) {
      if (!i.parentId || !inScope.has(i.id) || i.archivedAt) continue;
      const list = childrenOf.get(i.parentId);
      if (list) list.push(i);
      else childrenOf.set(i.parentId, [i]);
    }
    for (const list of childrenOf.values()) list.sort(byStart);
  }
  const rows: TimelineRow[] = [];
  const seen = new Set<string>();

  const pushItem = (item: ItemLite, depth: number, anchor: number | null, withChildren: boolean) => {
    if (seen.has(item.id)) return;
    seen.add(item.id);
    const kids = withChildren ? (childrenOf.get(item.id) ?? []) : [];
    const expanded = !opts.collapsed.has(item.id);
    const own = itemSpan(item);
    rows.push({
      kind: "item",
      id: item.id,
      item,
      depth,
      childCount: kids.length,
      expanded,
      rollup: own ? null : rollupOf(kids),
      anchor,
    });
    if (expanded) for (const k of kids) pushItem(k, depth + 1, anchor, depth < 2);
    else hide(kids);
  };
  // Descendants of a collapsed row stay hidden (later loops must not surface them at depth 0).
  const hide = (list: readonly ItemLite[]) => {
    for (const k of list) {
      if (seen.has(k.id)) continue;
      seen.add(k.id);
      hide(childrenOf.get(k.id) ?? []);
    }
  };

  if (opts.mode === "epics") {
    const level = (i: ItemLite) => catalog.typeById.get(i.typeId)?.level ?? 1;
    const tops = items.filter((i) => level(i) >= 2);
    const topIds = new Set(tops.map((i) => i.id));
    // Initiatives first, epics nested under their initiative when both match.
    const roots = tops.filter((i) => !i.parentId || !topIds.has(i.parentId)).sort(byStart);
    for (const r of roots) pushItem(r, 0, null, true);
    for (const t of tops.sort(byStart)) pushItem(t, 0, null, true);
    return { rows, unscheduled, scheduled };
  }

  if (opts.mode === "all") {
    const dated = items.filter((i) => i.startDate || i.dueDate);
    const datedIds = new Set(dated.map((i) => i.id));
    const roots = dated.filter((i) => !i.parentId || !datedIds.has(i.parentId) || !opts.showSubItems).sort(byStart);
    for (const r of roots) pushItem(r, 0, null, true);
    return { rows, unscheduled, scheduled };
  }

  // Milestone mode.
  const buckets = new Map<string, ItemLite[]>();
  for (const i of items) {
    const key = i.milestoneId ?? "none";
    const list = buckets.get(key);
    if (list) list.push(i);
    else buckets.set(key, [i]);
  }
  const productIds = new Set(items.map((i) => i.productId));
  const milestones = catalog.milestones
    .filter((ms) => buckets.has(ms.id) || (ms.status === "open" && productIds.has(ms.productId)))
    .sort(compareMilestones);
  const sections: Array<{ id: string; milestone: MilestoneView | null; items: ItemLite[] }> = [
    ...milestones.map((ms) => ({ id: `ms:${ms.id}`, milestone: ms, items: buckets.get(ms.id) ?? [] })),
    ...(buckets.has("none") ? [{ id: "ms:none", milestone: null, items: buckets.get("none")! }] : []),
  ];
  for (const s of sections) {
    const list = [...s.items].sort(byStart);
    const collapsed = opts.collapsed.has(s.id) !== (s.milestone === null || s.milestone.status !== "open");
    rows.push({
      kind: "section",
      id: s.id,
      milestone: s.milestone,
      count: list.length,
      collapsed,
      span: rollupOf(list),
    });
    if (collapsed) {
      for (const i of list) seen.add(i.id);
      continue;
    }
    const anchor = s.milestone?.targetDate ? dayOf(s.milestone.targetDate) : null;
    const ids = new Set(list.map((i) => i.id));
    for (const i of list) if (!i.parentId || !ids.has(i.parentId) || !opts.showSubItems) pushItem(i, 0, anchor, true);
  }
  return { rows, unscheduled, scheduled };
}

/** Visible day range: everything with a date plus today, padded per zoom. */
export function visibleRange(
  rows: readonly TimelineRow[],
  zoom: TimelineZoom,
  extra: number[] = [],
): { start: number; end: number } {
  const today = todayDay();
  let lo = today;
  let hi = today;
  const take = (s: Span | null) => {
    if (!s) return;
    lo = Math.min(lo, s.start);
    hi = Math.max(hi, s.end);
  };
  for (const r of rows) {
    if (r.kind === "section") {
      take(r.span);
      if (r.milestone?.targetDate) take({ start: dayOf(r.milestone.targetDate), end: dayOf(r.milestone.targetDate) });
    } else {
      take(itemSpan(r.item) ?? r.rollup);
      if (r.anchor !== null) take({ start: r.anchor, end: r.anchor });
    }
  }
  for (const d of extra) {
    lo = Math.min(lo, d);
    hi = Math.max(hi, d);
  }
  const pad = { weeks: 21, months: 60, quarters: 150 }[zoom];
  const minAhead = { weeks: 56, months: 240, quarters: 540 }[zoom];
  const start = Math.min(lo, today - Math.round(minAhead / 4)) - pad;
  const end = Math.max(hi, today + minAhead) + pad;
  // Align to the start of a week so day and week ticks sit on the grid.
  return { start: start - weekday(start), end };
}
